import {
  BUCKET_DATE_FILTER_PREFIX,
  type BucketDateRangeFilterDefinition,
  type BucketDateRangeFilterProperty,
  type BucketDateRangePreset,
  MAX_RANGE_SPAN,
  PRESET_FUTURE_2_DAYS,
  PRESET_FUTURE_30_DAYS,
  PRESET_FUTURE_7_DAYS,
  PRESET_FUTURE_ANY,
  PRESET_PAST_2_DAYS,
  PRESET_PAST_30_DAYS,
  PRESET_PAST_365_DAYS,
  PRESET_PAST_7_DAYS,
  PRESET_PAST_90_DAYS,
  PRESET_TODAY,
} from './bucket-date-presets'
import type { JobFilter, JobListSort } from '@ironsight/types'
import type { Hub } from '@/models/business-unit'
import { ITEMS_PER_PAGE_OPTIONS, type ItemsPerPageOptions } from './useJobListPagination'

export const BUCKET_FILTER_PROPERTIES = [
  'Status',
  'IsArchived',
  'Modified',
  'CompletedTime',
  'StartTime',
  'JobTimeWindow',
] as const

export const JOB_FILTER_BUCKET_IDS = {
  All: 'all',
  Available: 'available',
  Queue: 'queue',
  Dispatched: 'dispatched',
  Completed: 'completed',
  PendingApproval: 'pendingApproval',
  Rejected: 'rejected',
  Approved: 'approved',
  Following: 'following',
  Cancelled: 'cancelled',
  Archived: 'archived',
} as const

export const BUCKET_DATE_FILTER_PERSISTENCE_TTL_MS = 8 * 60 * 60 * 1000

export type BucketProperty = (typeof BUCKET_FILTER_PROPERTIES)[number]

export type JobFilterBucketId = (typeof JOB_FILTER_BUCKET_IDS)[keyof typeof JOB_FILTER_BUCKET_IDS]

// Not a `const enum`: esbuild compiles file-by-file and cannot inline const enum
// members across module boundaries, and this one is consumed from ExportDrawer,
// useExportDrawer and JobFilterBuckets.
export enum BUCKET_GROUP {
  Available = 0,
  Active = 1,
  Completed = 2,
  Following = 3,
  Closed = 4,
  All = 5,
}

export type BucketGroup = (typeof BUCKET_GROUP)[keyof typeof BUCKET_GROUP]

const BUCKET_DATE_RANGE_FILTER_PRESETS = {
  future: [
    PRESET_FUTURE_ANY,
    PRESET_TODAY,
    PRESET_FUTURE_2_DAYS,
    PRESET_FUTURE_7_DAYS,
    PRESET_FUTURE_30_DAYS,
  ],
  past: [
    PRESET_TODAY,
    PRESET_PAST_2_DAYS,
    PRESET_PAST_7_DAYS,
    PRESET_PAST_30_DAYS,
    PRESET_PAST_90_DAYS,
    PRESET_PAST_365_DAYS,
  ],
} as const satisfies Record<string, readonly BucketDateRangePreset[]>

const DEFAULT_PRESET_KEY: Record<keyof typeof BUCKET_DATE_RANGE_FILTER_PRESETS, string> = {
  future: PRESET_FUTURE_ANY.key,
  past: PRESET_PAST_90_DAYS.key,
} as const

const ALL_RESULTS_DATE_RANGE_PRESETS: readonly BucketDateRangePreset[] = [
  PRESET_TODAY,
  {
    key: 'all_results_yesterday',
    label: 'Yesterday',
    range: {
      start: { kind: 'Relative', offsetInDays: -1 },
      end: { kind: 'Relative', offsetInDays: -1 },
    },
  },
  {
    key: PRESET_PAST_7_DAYS.key,
    label: 'Last 7 days',
    range: {
      start: { kind: 'Relative', offsetInDays: -6 },
      end: { kind: 'Relative', offsetInDays: 0 },
    },
  },
  {
    key: 'all_results_tomorrow',
    label: 'Tomorrow',
    dividerBefore: true,
    range: {
      start: { kind: 'Relative', offsetInDays: 1 },
      end: { kind: 'Relative', offsetInDays: 1 },
    },
  },
  {
    key: PRESET_FUTURE_7_DAYS.key,
    label: 'Next 7 days',
    range: {
      start: { kind: 'Relative', offsetInDays: 0 },
      end: { kind: 'Relative', offsetInDays: 6 },
    },
  },
]

/**
 * Visible & a11y labels for filtering different Job buckets.
 */
const DATE_FILTER_LABEL: Record<BucketDateRangeFilterProperty, string> = {
  StartTime: 'Planned start date',
  CompletedTime: 'Completion date',
  Modified: 'Modified',
  JobTimeWindow: 'Planned/Ongoing',
} as const

export type BucketStatusFilterDefinition = {
  statuses: Array<{ value: string; label: string }>
}

export type BucketDefinition = {
  id: string
  label: string
  icon: string
  group: BucketGroup
  dateRangeFilter?: BucketDateRangeFilterDefinition
  statusFilter?: BucketStatusFilterDefinition
  defaultSort: JobListSort
  currentPage: number
  itemsPerPage: ItemsPerPageOptions
  allowedSortFields?: ReadonlyArray<JobListSort['field']>
}

export type BucketDefinitionsContext = {
  accessibleHubIds?: readonly string[]
  organizationId?: string | null
  hubs?: readonly Hub[]
  isHubsLoading?: boolean
}

type BucketRole = 'producer' | 'provider' | 'unresolved'

type BucketResolution = {
  role: BucketRole
  definitions: readonly BucketDefinition[]
  defaultBucket?: BucketDefinition
}

export const BUCKET_STATUS_FILTER_PREFIX = 'bucket_status__'

export function isBucketDateFilter(filter: Pick<JobFilter, 'id'>): boolean {
  return filter.id.startsWith(BUCKET_DATE_FILTER_PREFIX)
}

export function isBucketStatusFilter(filter: Pick<JobFilter, 'id'>): boolean {
  return filter.id.startsWith(BUCKET_STATUS_FILTER_PREFIX)
}

export function getBucketStatusFilterId(bucketId: string): string {
  return `${BUCKET_STATUS_FILTER_PREFIX}${bucketId}`
}

export function isBucketManagedProperty(filter: JobFilter): boolean {
  return BUCKET_FILTER_PROPERTIES.includes(filter.property as BucketProperty)
}

export function getBucketDateFilterId(bucketId: string): string {
  return `${BUCKET_DATE_FILTER_PREFIX}${bucketId}`
}

export function getBucketIdFromDateFilterId(filterId: string): string | null {
  if (!isBucketDateFilter({ id: filterId })) return null
  const bucketId = filterId.slice(BUCKET_DATE_FILTER_PREFIX.length)
  return bucketId.length ? bucketId : null
}

function getBucketRole(context?: BucketDefinitionsContext): BucketRole {
  if (!context?.organizationId || context?.isHubsLoading) {
    return 'unresolved'
  }

  const { accessibleHubIds, hubs, organizationId } = context

  if (!accessibleHubIds || !hubs) {
    return 'unresolved'
  }

  return accessibleHubIds.some((hubId) => {
    const hub = hubs.find((h) => h.id === hubId)
    return hub?.ownerId === organizationId
  })
    ? 'producer'
    : 'provider'
}

export function createBucketDefinitions(isProducer: boolean) {
  const allBucketJobTimeWindowConfig: BucketDateRangeFilterDefinition = {
    property: 'JobTimeWindow',
    type: 'DateTime',
    label: DATE_FILTER_LABEL.JobTimeWindow,
    limits: { maxSpanDays: MAX_RANGE_SPAN },
    presets: ALL_RESULTS_DATE_RANGE_PRESETS,
    defaultPresetKey: PRESET_TODAY.key,
  }

  const allBucket: BucketDefinition = {
    id: JOB_FILTER_BUCKET_IDS.All,
    label: 'All results',
    icon: 'all_inbox',
    group: BUCKET_GROUP.All,
    dateRangeFilter: allBucketJobTimeWindowConfig,
    defaultSort: { field: 'Modified', direction: 'desc' },
    currentPage: 1,
    itemsPerPage: ITEMS_PER_PAGE_OPTIONS[0],
  }

  const availableBucket: BucketDefinition = {
    id: JOB_FILTER_BUCKET_IDS.Available,
    label: 'Available',
    icon: 'markunread_mailbox',
    group: BUCKET_GROUP.Available,
    dateRangeFilter: {
      property: 'StartTime',
      type: 'DateTime',
      label: DATE_FILTER_LABEL.StartTime,
      presets: BUCKET_DATE_RANGE_FILTER_PRESETS.future,
      defaultPresetKey: DEFAULT_PRESET_KEY.future,
    },
    defaultSort: { field: 'Created', direction: 'desc' },
    currentPage: 1,
    itemsPerPage: ITEMS_PER_PAGE_OPTIONS[0],
  }

  const queueStatusFilter: BucketStatusFilterDefinition | undefined = isProducer
    ? {
        statuses: [
          { value: 'Requested', label: 'Requested' },
          { value: 'AssignedToPool', label: 'Assigned To Pool' },
        ],
      }
    : undefined

  const dispatchedStatusFilter: BucketStatusFilterDefinition = isProducer
    ? {
        statuses: [
          {
            value: 'AssignedToServiceProvider',
            label: 'Assigned To Service Provider',
          },
          { value: 'Scheduled', label: 'Scheduled' },
          { value: 'Active', label: 'Active' },
          { value: 'Paused', label: 'Paused' },
        ],
      }
    : {
        statuses: [
          { value: 'Scheduled', label: 'Scheduled' },
          { value: 'Active', label: 'Active' },
          { value: 'Paused', label: 'Paused' },
        ],
      }

  const followingStatusFilter: BucketStatusFilterDefinition = {
    statuses: [
      { value: 'Requested', label: 'Requested' },
      { value: 'AssignedToPool', label: 'Assigned To Pool' },
      {
        value: 'AssignedToServiceProvider',
        label: 'Assigned To Service Provider',
      },
      { value: 'Scheduled', label: 'Scheduled' },
      { value: 'Active', label: 'Active' },
      { value: 'Paused', label: 'Paused' },
      { value: 'Completed', label: 'Completed' },
      { value: 'PendingApproval', label: 'Pending Approval' },
      { value: 'Rejected', label: 'Rejected' },
      { value: 'Approved', label: 'Approved' },
      { value: 'Cancelled', label: 'Cancelled' },
    ],
  }

  const buckets: BucketDefinition[] = [
    allBucket,
    ...(!isProducer ? [availableBucket] : []),
    {
      id: JOB_FILTER_BUCKET_IDS.Queue,
      label: 'Queued',
      icon: 'inbox',
      group: BUCKET_GROUP.Active,
      dateRangeFilter: {
        property: 'StartTime',
        type: 'DateTime',
        label: DATE_FILTER_LABEL.StartTime,
        presets: BUCKET_DATE_RANGE_FILTER_PRESETS.future,
        defaultPresetKey: DEFAULT_PRESET_KEY.future,
      },
      statusFilter: queueStatusFilter,
      defaultSort: { field: 'Created', direction: 'desc' },
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE_OPTIONS[0],
    },
    {
      id: JOB_FILTER_BUCKET_IDS.Dispatched,
      label: 'Dispatched',
      icon: 'view_timeline',
      group: BUCKET_GROUP.Active,
      dateRangeFilter: {
        property: 'StartTime',
        type: 'DateTime',
        label: DATE_FILTER_LABEL.StartTime,
        presets: BUCKET_DATE_RANGE_FILTER_PRESETS.future,
        defaultPresetKey: DEFAULT_PRESET_KEY.future,
      },
      statusFilter: dispatchedStatusFilter,
      defaultSort: { field: 'Created', direction: 'desc' },
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE_OPTIONS[0],
    },
    {
      id: JOB_FILTER_BUCKET_IDS.Completed,
      label: 'Completed',
      icon: 'event_available',
      group: BUCKET_GROUP.Completed,
      dateRangeFilter: {
        property: 'CompletedTime',
        type: 'DateTime',
        label: DATE_FILTER_LABEL.CompletedTime,
        limits: {
          maxSpanDays: MAX_RANGE_SPAN,
        },
        presets: BUCKET_DATE_RANGE_FILTER_PRESETS.past,
        defaultPresetKey: DEFAULT_PRESET_KEY.past,
      },
      defaultSort: { field: 'CompletedTime', direction: 'desc' },
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE_OPTIONS[0],
    },
    {
      id: JOB_FILTER_BUCKET_IDS.PendingApproval,
      label: 'Pending Approval',
      icon: 'thumbs_up_down',
      group: BUCKET_GROUP.Completed,
      dateRangeFilter: {
        property: 'CompletedTime',
        type: 'DateTime',
        label: DATE_FILTER_LABEL.CompletedTime,
        limits: {
          maxSpanDays: MAX_RANGE_SPAN,
        },
        presets: BUCKET_DATE_RANGE_FILTER_PRESETS.past,
        defaultPresetKey: DEFAULT_PRESET_KEY.past,
      },
      defaultSort: { field: 'CompletedTime', direction: 'desc' },
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE_OPTIONS[0],
    },
    {
      id: JOB_FILTER_BUCKET_IDS.Rejected,
      label: 'Rejected',
      icon: 'thumb_down',
      group: BUCKET_GROUP.Completed,
      dateRangeFilter: {
        property: 'CompletedTime',
        type: 'DateTime',
        label: DATE_FILTER_LABEL.CompletedTime,
        limits: {
          maxSpanDays: MAX_RANGE_SPAN,
        },
        presets: BUCKET_DATE_RANGE_FILTER_PRESETS.past,
        defaultPresetKey: DEFAULT_PRESET_KEY.past,
      },
      defaultSort: { field: 'CompletedTime', direction: 'desc' },
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE_OPTIONS[0],
    },
    {
      id: JOB_FILTER_BUCKET_IDS.Approved,
      label: 'Approved',
      icon: 'done_all',
      group: BUCKET_GROUP.Completed,
      dateRangeFilter: {
        property: 'CompletedTime',
        type: 'DateTime',
        label: DATE_FILTER_LABEL.CompletedTime,
        limits: {
          maxSpanDays: MAX_RANGE_SPAN,
        },
        presets: BUCKET_DATE_RANGE_FILTER_PRESETS.past,
        defaultPresetKey: DEFAULT_PRESET_KEY.past,
      },
      defaultSort: { field: 'CompletedTime', direction: 'desc' },
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE_OPTIONS[0],
    },
    {
      id: JOB_FILTER_BUCKET_IDS.Following,
      label: 'Following',
      icon: 'group_add',
      group: BUCKET_GROUP.Following,
      dateRangeFilter: {
        property: 'StartTime',
        type: 'DateTime',
        label: DATE_FILTER_LABEL.StartTime,
        limits: {
          maxSpanDays: MAX_RANGE_SPAN,
        },
        presets: BUCKET_DATE_RANGE_FILTER_PRESETS.past,
        defaultPresetKey: DEFAULT_PRESET_KEY.past,
      },
      statusFilter: followingStatusFilter,
      defaultSort: { field: 'Created', direction: 'desc' },
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE_OPTIONS[0],
    },
    {
      id: JOB_FILTER_BUCKET_IDS.Cancelled,
      label: 'Cancelled',
      icon: 'cancel',
      group: BUCKET_GROUP.Closed,
      dateRangeFilter: {
        property: 'CompletedTime',
        type: 'DateTime',
        /** Custom label for this bucket to avoid confusion. */
        label: 'Cancelled date',
        limits: {
          maxSpanDays: 90,
        },
        /**
         * This bucket hasn't been performance tested with a 1-year cap yet.
         * Once this is evaluated it can be changed to use the `.past` filters.
         */
        presets: [
          PRESET_TODAY,
          PRESET_PAST_2_DAYS,
          PRESET_PAST_7_DAYS,
          PRESET_PAST_30_DAYS,
          PRESET_PAST_90_DAYS,
        ],
        defaultPresetKey: PRESET_PAST_90_DAYS.key,
      },
      defaultSort: { field: 'CompletedTime', direction: 'desc' },
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE_OPTIONS[0],
    },
    {
      id: JOB_FILTER_BUCKET_IDS.Archived,
      label: 'Archived',
      icon: 'archive',
      group: BUCKET_GROUP.Closed,
      dateRangeFilter: {
        property: 'StartTime',
        type: 'DateTime',
        label: DATE_FILTER_LABEL.StartTime,
        limits: {
          maxSpanDays: 90,
        },
        /**
         * This bucket hasn't been performance tested with a 1-year cap yet.
         * Once this is evaluated it can be changed to use the `.past` filters.
         */
        presets: [
          PRESET_TODAY,
          PRESET_PAST_2_DAYS,
          PRESET_PAST_7_DAYS,
          PRESET_PAST_30_DAYS,
          PRESET_PAST_90_DAYS,
        ],
        defaultPresetKey: PRESET_PAST_90_DAYS.key,
      },
      defaultSort: { field: 'Created', direction: 'desc' },
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE_OPTIONS[0],
    },
  ]

  return buckets as readonly BucketDefinition[]
}

const providerBucketDefinitions = createBucketDefinitions(false)
const producerBucketDefinitions = createBucketDefinitions(true)
const sharedDefaultBucket = providerBucketDefinitions.find(
  (bucket) => bucket.id === JOB_FILTER_BUCKET_IDS.All,
)
const getUnresolvedBucketDefinitions = () => {
  const bucket = sharedDefaultBucket
  return bucket ? [bucket] : []
}
const bucketGroupById = [...providerBucketDefinitions, ...producerBucketDefinitions].reduce<
  Record<string, BucketGroup>
>((groups, bucket) => {
  groups[bucket.id] = bucket.group
  return groups
}, {})

export function getBucketGroupForBucketId(bucketId: string): BucketGroup | null {
  return bucketGroupById[bucketId] ?? null
}

function resolveBucketResolution(context?: BucketDefinitionsContext): BucketResolution {
  const role = getBucketRole(context)

  if (role === 'unresolved') {
    return {
      role,
      definitions: getUnresolvedBucketDefinitions(),
      defaultBucket: sharedDefaultBucket,
    }
  }

  if (role === 'producer') {
    return {
      role,
      definitions: producerBucketDefinitions,
      defaultBucket: producerBucketDefinitions.find(
        (bucket) => bucket.id === JOB_FILTER_BUCKET_IDS.Queue,
      ),
    }
  }

  return {
    role,
    definitions: providerBucketDefinitions,
    defaultBucket: providerBucketDefinitions.find(
      (bucket) => bucket.id === JOB_FILTER_BUCKET_IDS.Available,
    ),
  }
}

export function useJobFilterBuckets() {
  function getBucketDefinitions(context?: BucketDefinitionsContext) {
    return resolveBucketResolution(context).definitions
  }

  function getDefaultBucket(context?: BucketDefinitionsContext): BucketDefinition | undefined {
    return resolveBucketResolution(context).defaultBucket
  }

  return {
    getBucketDefinitions,
    getDefaultBucket,
  }
}
